/**
 * useStoreVendorTerms: a store's standing on GearGhost's vendor terms, and
 * what the terms window does with it (docs/plans/taxes.md, Decisions 46, 47
 * and 53): tell the server the text is on the screen, and accept the
 * version in force. The dashboard's New Vendor Terms banner accepts through
 * it, and the Vendor terms row on Account Standing reads the standing and
 * opens accepted terms to read again, so the two cannot record them
 * differently.
 *
 * The standing is the server's (GET vendor/terms); nothing here works out
 * whether a store has accepted. `skip` leaves it unasked, for someone who
 * has no store.
 */
import { useCallback } from 'react'
import {
  useAcceptVendorTermsMutation,
  useGetVendorTermsStandingQuery,
  useMarkVendorTermsSeenMutation,
} from '@/redux/api/vendorApi'
import { asApiError } from '@/redux/api/axiosBaseQuery'
import { toastError, toastSuccess } from '@/utils/toast.jsx'

export const useStoreVendorTerms = ({ skip = false }: { skip?: boolean } = {}) => {
  const { data: standing, isError, refetch } = useGetVendorTermsStandingQuery(undefined, { skip })
  const [acceptTerms, { isLoading: accepting }] = useAcceptVendorTermsMutation()
  const [markSeen] = useMarkVendorTermsSeenMutation()

  // The text is on the vendor's screen: tell the server, which keeps the
  // first time (Decision 53). Nothing here waits on the answer. A call that
  // fails is made again the next time the text opens, and an accept records
  // it too, so no store accepts without having been recorded as seeing.
  // Stable, as VendorTermsModal's onShown has to be.
  const version = standing?.version
  const reportSeen = useCallback(() => {
    if (version !== undefined) void markSeen({ version })
  }, [markSeen, version])

  /**
   * Accept the version the window showed, and say how it went. Answers
   * whether the store accepted, so a caller can go on (the banner returns
   * the vendor to the store page that sent them). A version no longer in force
   * is refused; the standing is read again and the next opening shows the
   * new text.
   */
  const accept = async (shownVersion: number): Promise<boolean> => {
    try {
      await acceptTerms({ version: shownVersion }).unwrap()
      toastSuccess('Terms of Service accepted')
      return true
    } catch (err) {
      const { message } = asApiError(err)
      toastError(message === undefined ? 'The terms could not be accepted. Try again.' : message)
      return false
    }
  }

  return { standing, isError, refetch, accepting, reportSeen, accept }
}
